import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { InstanceSnapshot } from '@/lib/api';
import { useAuth } from './useAuth';

export type LiveStatus = 'connecting' | 'live' | 'offline';

interface LiveState {
  snapshots: Record<string, InstanceSnapshot>;
  status: LiveStatus;
}

const LiveContext = createContext<LiveState>({ snapshots: {}, status: 'connecting' });

/**
 * Haelt genau eine SSE-Verbindung fuer die gesamte Anwendung.
 *
 * Das Backend pollt `wg show dump` zentral und schiebt Snapshots aktiv an alle
 * verbundenen Dashboards - die Oberflaeche muss also nicht selbst pollen.
 * Bricht die Verbindung ab, wird mit wachsendem Abstand neu verbunden.
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [snapshots, setSnapshots] = useState<Record<string, InstanceSnapshot>>({});
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const retryRef = useRef(0);

  useEffect(() => {
    if (!user) {
      setSnapshots({});
      setStatus('connecting');
      return;
    }

    let source: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let closed = false;

    const connect = () => {
      source = new EventSource('/api/monitor/stream', { withCredentials: true });

      source.addEventListener('hello', () => {
        retryRef.current = 0;
        setStatus('live');
      });

      source.addEventListener('snapshot', (event) => {
        try {
          const snapshot = JSON.parse((event as MessageEvent<string>).data) as InstanceSnapshot;
          setSnapshots((previous) => ({ ...previous, [snapshot.instanceId]: snapshot }));
        } catch {
          // Defekte Nachricht verwerfen - der naechste Snapshot folgt in Kuerze
        }
      });

      source.onerror = () => {
        if (closed) return;
        setStatus('offline');
        source?.close();

        // Exponentielles Backoff, gedeckelt auf 30 Sekunden
        const delay = Math.min(30_000, 1000 * 2 ** retryRef.current);
        retryRef.current += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      source?.close();
    };
  }, [user]);

  const value = useMemo(() => ({ snapshots, status }), [snapshots, status]);
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive(): LiveState {
  return useContext(LiveContext);
}

/** Live-Daten einer einzelnen Instanz. */
export function useLiveInstance(instanceId: string | undefined): InstanceSnapshot | null {
  const { snapshots } = useLive();
  return instanceId ? (snapshots[instanceId] ?? null) : null;
}
